import 'react-error-overlay';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/react';

import App from './App';
import reportWebVitals from './reportWebVitals';
import { CollapseDrawerProvider } from './contexts/drawer.context';
import { ENVS } from './http';
import { isProdEnv } from './services/sentry-replay.service';
import { sentryTunnelUrl } from './services/sentry-tunnel';
import dayjs from 'dayjs';

var localizedFormat = require('dayjs/plugin/localizedFormat')
dayjs.extend(localizedFormat)

// Self-hosted Sentry: errors, performance, distributed tracing, and Session Replay.
// DSN comes from the build env (REACT_APP_SENTRY_DSN); when it is unset the SDK
// is a no-op, so local builds send nothing. Source maps are uploaded by
// amplify.yml under the same release value set here.
// Replay samples 10% of anonymous sessions and 100% of sessions that hit an
// error; authenticated sessions are additionally force-started to 100% via
// startAuthenticatedReplay() at the auth.service.ts login/sso/whoIam points
// (per Carlos, 9/21) since replaysSessionSampleRate can only set one flat
// rate at page load and can't itself branch on auth state.
// Replay recording is PROD-only per Carlos (9/17): dev/QA/localhost still get
// errors + performance, but replaysSessionSampleRate/replaysOnErrorSampleRate
// are forced to 0 everywhere else so nobody's local or dev session is screen-
// recorded.

// Distributed tracing target: the pom-2-api origin for whichever env this
// build is running against, so the SDK adds sentry-trace/baggage headers to
// API calls and frontend spans link to the matching backend spans. Derived
// from the same ENVS map http/index.ts uses for its axios baseURL, so dev
// builds trace against aware-api-dev and prod builds trace against aware-api.
const apiOrigin = new URL(ENVS[process.env.REACT_APP_ENV || 'dev'].BASE_URL).origin;

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  // Routes envelopes through a URL with no ?sentry_key=/?sentry_version= in
  // the query string, so uBlock Origin/EasyPrivacy (which block on that
  // pattern, not on our domain) don't drop them. See sentry-tunnel.ts.
  tunnel: sentryTunnelUrl(process.env.REACT_APP_SENTRY_DSN),
  environment: process.env.REACT_APP_ENV || 'dev',
  release: process.env.REACT_APP_SENTRY_RELEASE,
  // blockAllMedia/maskAllText default to true (Sentry masks images/video/audio
  // and all on-screen text for privacy); both off here per Carlos so replays
  // show real content instead of blank boxes and black bars, for debugging.
  // Note: this app displays customer PII (names, addresses, safety-report
  // detail) -- maskAllText:false means that now lands in Sentry recordings.
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({ blockAllMedia: false, maskAllText: false }),
    Sentry.browserProfilingIntegration(),
  ],
  tracesSampleRate: 0.2,
  tracePropagationTargets: [apiOrigin],
  replaysSessionSampleRate: isProdEnv ? 0.1 : 0,
  replaysOnErrorSampleRate: isProdEnv ? 1.0 : 0,
  // Release health (crash-free rate per deploy) is tracked automatically from
  // the `release` build-SHA tag above; no separate opt-in in this SDK version.
  // Profiling: only runs while a sampled trace is open (profileLifecycle:
  // 'trace'), so it rides on tracesSampleRate above. Chrome/Edge only --
  // Safari/Firefox don't implement the JS Self-Profiling API and send
  // nothing. profilesSampleRate is deprecated in 10.75; use these two.
  profileSessionSampleRate: isProdEnv ? 0.1 : 1.0,
  profileLifecycle: 'trace',
});

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <HelmetProvider>
    <CollapseDrawerProvider>
      <BrowserRouter>
        <Sentry.ErrorBoundary fallback={<p>Something went wrong</p>}>
          <App />
        </Sentry.ErrorBoundary>
      </BrowserRouter>
    </CollapseDrawerProvider>
  </HelmetProvider>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
